import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import '../../styles/analytics/Analytics.css';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);
const api = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    withCredentials: true,
  });

export default function Analytics() {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dateRange, setDateRange] = useState('all');


    useEffect(() => {
        fetchAnalytics(dateRange);
    }, [dateRange]);

    const fetchAnalytics = async (range) => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get(`/api/analytics?range=${range}`);
            if (response.data.success) {
                setAnalyticsData(response.data.data);
            } else {
                setError('Kunne ikke hente analytics data');
            }
        } catch (err) {
            console.error('Error fetching analytics:', err);
            if (err.response?.status === 403) {
                setError('Du har ikke tilgang til analytics');
            } else {
                setError('Kunne ikke hente analytics data');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDateRangeChange = (e) => {
        setDateRange(e.target.value);
    };

    if (loading) {
        return (
            <div className="analytics-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Laster analytics...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="analytics-container">
                <div className="error-message">{error}</div>
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til Dashboard
                </Link>
            </div>
        );
    }

    if (!analyticsData) {
        return (
            <div className="analytics-container">
                <div className="error-message">Ingen data tilgjengelig</div>
            </div>
        );
    }

    // Prepare user creation chart data
    const userCreationChartData = {
        labels: analyticsData.userCreationStats.map(stat => stat.month),
        datasets: [
            {
                label: 'Nye brukere',
                data: analyticsData.userCreationStats.map(stat => stat.count),
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                tension: 0.1
            }
        ]
    };

    // Prepare subscription chart data
    const subscriptionChartData = {
        labels: ['Stripe', 'iOS', 'Ukjent'],
        datasets: [
            {
                label: 'Abonnementer',
                data: [
                    analyticsData.subscriptionStats.stripe,
                    analyticsData.subscriptionStats.ios,
                    analyticsData.subscriptionStats.unknown
                ],
                backgroundColor: [
                    'rgba(54, 162, 235, 0.8)',
                    'rgba(255, 99, 132, 0.8)',
                    'rgba(201, 203, 207, 0.8)'
                ],
                borderColor: [
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 99, 132, 1)',
                    'rgba(201, 203, 207, 1)'
                ],
                borderWidth: 1
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Bruker opprettelser per måned'
            }
        }
    };

    const subscriptionChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Abonnementer etter type'
            }
        }
    };

    // Prepare level chart data
    const levelChartData = analyticsData.levelStats && analyticsData.levelStats.length > 0 ? {
        labels: analyticsData.levelStats.map(stat => stat.level),
        datasets: [
            {
                label: 'Brukere',
                data: analyticsData.levelStats.map(stat => stat.count),
                backgroundColor: [
                    'rgba(75, 192, 192, 0.8)',
                    'rgba(54, 162, 235, 0.8)',
                    'rgba(255, 206, 86, 0.8)',
                    'rgba(255, 99, 132, 0.8)',
                    'rgba(153, 102, 255, 0.8)',
                    'rgba(201, 203, 207, 0.8)'
                ],
                borderColor: [
                    'rgba(75, 192, 192, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(255, 99, 132, 1)',
                    'rgba(153, 102, 255, 1)',
                    'rgba(201, 203, 207, 1)'
                ],
                borderWidth: 1
            }
        ]
    } : null;

    // Prepare sex chart data
    const sexChartData = analyticsData.sexStats && analyticsData.sexStats.length > 0 ? {
        labels: analyticsData.sexStats.map(stat => {
            if (stat.sex === 'mann') return 'Mann';
            if (stat.sex === 'kvinne') return 'Kvinne';
            if (stat.sex === 'annet') return 'Annet';
            return stat.sex;
        }),
        datasets: [
            {
                label: 'Brukere',
                data: analyticsData.sexStats.map(stat => stat.count),
                backgroundColor: [
                    'rgba(54, 162, 235, 0.8)',
                    'rgba(255, 99, 132, 0.8)',
                    'rgba(153, 102, 255, 0.8)',
                    'rgba(201, 203, 207, 0.8)'
                ],
                borderColor: [
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 99, 132, 1)',
                    'rgba(153, 102, 255, 1)',
                    'rgba(201, 203, 207, 1)'
                ],
                borderWidth: 1
            }
        ]
    } : null;

    // Prepare age chart data
    const ageChartData = analyticsData.ageStats && analyticsData.ageStats.length > 0 ? {
        labels: analyticsData.ageStats.map(stat => {
            if (stat.age === 'above 35') return 'Over 35 år';
            if (stat.age === 'under 35') return 'Under 35 år';
            return stat.age;
        }),
        datasets: [
            {
                label: 'Brukere',
                data: analyticsData.ageStats.map(stat => stat.count),
                backgroundColor: [
                    'rgba(75, 192, 192, 0.8)',
                    'rgba(255, 206, 86, 0.8)',
                    'rgba(201, 203, 207, 0.8)'
                ],
                borderColor: [
                    'rgba(75, 192, 192, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(201, 203, 207, 1)'
                ],
                borderWidth: 1
            }
        ]
    } : null;

    const doughnutChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            }
        }
    };

    // Prepare daily active users chart data
    const dailyActiveUsersChartData = analyticsData.dailyActiveUsersChart && analyticsData.dailyActiveUsersChart.length > 0 ? {
        labels: analyticsData.dailyActiveUsersChart.map(stat => {
            const date = new Date(stat.date);
            return date.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit' });
        }),
        datasets: [
            {
                label: 'Daglig aktive brukere',
                data: analyticsData.dailyActiveUsersChart.map(stat => stat.count),
                borderColor: 'rgb(255, 99, 132)',
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                tension: 0.1,
                fill: true
            }
        ]
    } : null;

    const dailyActiveUsersChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Daglig aktive brukere'
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1
                }
            }
        }
    };

    return (
        <div className="analytics-container">
            <div className="analytics-header">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til Dashboard
                </Link>
                <h1>Analytics Dashboard</h1>
                <div className="date-range-selector">
                    <label htmlFor="dateRange">Tidsperiode:</label>
                    <select
                        id="dateRange"
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        className="date-range-select"
                    >
                        <option value="all">Alt</option>
                        <option value="30days">Siste 30 dager</option>
                        <option value="3months">Siste 3 måneder</option>
                        <option value="year">Siste år</option>
                    </select>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="stats-cards">
                <div className="stat-card">
                    <div className="stat-icon">
                        <i className="fas fa-users"></i>
                    </div>
                    <div className="stat-content">
                        <h3>Totalt brukere</h3>
                        <p className="stat-value">{analyticsData.totalUsers.toLocaleString()}</p>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">
                        <i className="fas fa-star"></i>
                    </div>
                    <div className="stat-content">
                        <h3>Favorittaktiviteter</h3>
                        <p className="stat-value">{analyticsData.favoriteActivities.toLocaleString()}</p>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">
                        <i className="fas fa-check-circle"></i>
                    </div>
                    <div className="stat-content">
                        <h3>Fullførte aktiviteter</h3>
                        <p className="stat-value">{analyticsData.finishedActivities.toLocaleString()}</p>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">
                        <i className="fas fa-user-check"></i>
                    </div>
                    <div className="stat-content">
                        <h3>Daglig aktive brukere</h3>
                        <p className="stat-value">{analyticsData.dailyActiveUsers.toLocaleString()}</p>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">
                        <i className="fas fa-route"></i>
                    </div>
                    <div className="stat-content">
                        <h3>Brukere med programfremgang</h3>
                        <p className="stat-value">{(analyticsData.usersWithProgramProgress ?? 0).toLocaleString()}</p>
                    </div>
                </div>
            </div>

            {/* Charts */}
            <div className="charts-container">
                <div className="chart-card">
                    <div className="chart-wrapper">
                        <Line data={userCreationChartData} options={chartOptions} />
                    </div>
                </div>

                {dailyActiveUsersChartData && (
                    <div className="chart-card">
                        <div className="chart-wrapper">
                            <Line data={dailyActiveUsersChartData} options={dailyActiveUsersChartOptions} />
                        </div>
                    </div>
                )}

                <div className="chart-card">
                    <div className="chart-wrapper">
                        <Doughnut data={subscriptionChartData} options={subscriptionChartOptions} />
                    </div>
                </div>

                {levelChartData && (
                    <div className="chart-card">
                        <div className="chart-wrapper">
                            <Doughnut 
                                data={levelChartData} 
                                options={{
                                    ...doughnutChartOptions,
                                    plugins: {
                                        ...doughnutChartOptions.plugins,
                                        title: {
                                            display: true,
                                            text: 'Brukere etter nivå'
                                        }
                                    }
                                }} 
                            />
                        </div>
                    </div>
                )}

                {sexChartData && (
                    <div className="chart-card">
                        <div className="chart-wrapper">
                            <Doughnut 
                                data={sexChartData} 
                                options={{
                                    ...doughnutChartOptions,
                                    plugins: {
                                        ...doughnutChartOptions.plugins,
                                        title: {
                                            display: true,
                                            text: 'Brukere etter kjønn'
                                        }
                                    }
                                }} 
                            />
                        </div>
                    </div>
                )}

                {ageChartData && (
                    <div className="chart-card">
                        <div className="chart-wrapper">
                            <Doughnut 
                                data={ageChartData} 
                                options={{
                                    ...doughnutChartOptions,
                                    plugins: {
                                        ...doughnutChartOptions.plugins,
                                        title: {
                                            display: true,
                                            text: 'Brukere etter alder'
                                        }
                                    }
                                }} 
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Popular Activities Lists */}
            <div className="popular-activities-container">
                <div className="popular-activities-card">
                    <h2 className="popular-activities-title">
                        <i className="fas fa-star"></i> Mest favoriserte aktiviteter
                    </h2>
                    {analyticsData.mostFavoritedActivities && analyticsData.mostFavoritedActivities.length > 0 ? (
                        <div className="activities-list">
                            {analyticsData.mostFavoritedActivities.map((activity, index) => (
                                <div key={activity.id} className="activity-item">
                                    <div className="activity-rank">{index + 1}</div>
                                    <div className="activity-info">
                                        <h4 className="activity-name">{activity.name}</h4>
                                        {activity.shortDescription && (
                                            <p className="activity-description">{activity.shortDescription}</p>
                                        )}
                                        <div className="activity-meta">
                                            {activity.minutes && (
                                                <span className="activity-meta-item">
                                                    <i className="fas fa-clock"></i> {activity.minutes} min
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="activity-count">
                                        <span className="count-value">{activity.count}</span>
                                        <span className="count-label">favoritter</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="no-data">Ingen favorittaktiviteter ennå</p>
                    )}
                </div>

                <div className="popular-activities-card">
                    <h2 className="popular-activities-title">
                        <i className="fas fa-check-circle"></i> Mest fullførte aktiviteter
                    </h2>
                    {analyticsData.mostFinishedActivities && analyticsData.mostFinishedActivities.length > 0 ? (
                        <div className="activities-list">
                            {analyticsData.mostFinishedActivities.map((activity, index) => (
                                <div key={activity.id} className="activity-item">
                                    <div className="activity-rank">{index + 1}</div>
                                    <div className="activity-info">
                                        <h4 className="activity-name">{activity.name}</h4>
                                        {activity.shortDescription && (
                                            <p className="activity-description">{activity.shortDescription}</p>
                                        )}
                                        <div className="activity-meta">
                                            {activity.minutes && (
                                                <span className="activity-meta-item">
                                                    <i className="fas fa-clock"></i> {activity.minutes} min
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="activity-count">
                                        <span className="count-value">{activity.count}</span>
                                        <span className="count-label">fullført</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="no-data">Ingen fullførte aktiviteter ennå</p>
                    )}
                </div>

                <div className="popular-activities-card">
                    <h2 className="popular-activities-title">
                        <i className="fas fa-route"></i> Mest aktive program
                    </h2>
                    {analyticsData.programProgressStats && analyticsData.programProgressStats.length > 0 ? (
                        <div className="activities-list">
                            {analyticsData.programProgressStats.map((program, index) => (
                                <div key={program.programId} className="activity-item">
                                    <div className="activity-rank">{index + 1}</div>
                                    <div className="activity-info">
                                        <h4 className="activity-name">{program.programName}</h4>
                                        <div className="activity-meta">
                                            <span className="activity-meta-item">
                                                <i className="fas fa-users"></i> {program.activeUsers} brukere
                                            </span>
                                        </div>
                                    </div>
                                    <div className="activity-count">
                                        <span className="count-value">{program.completedDays}</span>
                                        <span className="count-label">dager fullført</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="no-data">Ingen programfremgang registrert ennå</p>
                    )}
                </div>
            </div>
        </div>
    );
}

